import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppBar, CircularProgress, Divider, Toolbar, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import colors from '../../../../scss/colors';
import logo from '../../../../Assets/images/Airswift Primary Logo (Black Text).png';
import classes from './Header.module.scss';
import { getTandEPendingCounts } from '../../../../actions/timesheet';
import {
  AccountCircleOutlined,
  AttachMoneyOutlined,
  DvrOutlined,
  ExitToApp,
  HelpOutlineOutlined,
  HomeOutlined,
  HouseOutlined,
  Login,
  Logout,
  PublicOutlined,
  SettingsOutlined,
  SupportOutlined,
  TimerOutlined,
  WorkOutline
} from '@mui/icons-material';
import { handleLogin, handleLogout } from '../../../../utils/loginFunctions';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useWindowSize } from '../../../../utils/hooks';
import { maxMobileSize, responsiveSizeJobs } from '../../../../utils/constants';
import LanguageSwitcher from '../../../languageSwitcher/LanguageSwitcher';

export default function Header({ showMenu = true }) {
  const { width } = useWindowSize();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [isBeingSignedIn, setIsBeingSignedIn] = useState(false);

  const accessTo = useSelector((state) => state.accessToParts);
  const pendingTandECounts = useSelector((state) => state.timesheet?.tandecounts);

  const allMenuItems = [
    {
      name: 'Home',
      path: isAuthenticated ? '/dashboard' : '/',
      icon: <HomeOutlined sx={{ fontSize: '28px' }} />,
      show: showMenu
    },
    {
      name: 'Jobs',
      path: isAuthenticated ? '/dashboard/jobs' : '/jobs',
      icon: <WorkOutline sx={{ fontSize: '28px' }} />,
      show: !accessTo.tAndEApprover && showMenu
    },
    {
      name: 'Onboarding',
      path: '/onboardingInteraction',
      icon: <DvrOutlined sx={{ fontSize: '28px' }} />,
      show: accessTo.onboarding && showMenu
    },
    {
      name: 'Time',
      path: '/dashboard/timesheets',
      icon: <TimerOutlined sx={{ fontSize: '28px' }} />,
      show: (accessTo.tAndEApprover || accessTo.tAndEContractor) && showMenu
    },
    {
      name: 'Expense',
      path: '/dashboard/expenses',
      icon: <AttachMoneyOutlined sx={{ fontSize: '28px' }} />,
      show: (accessTo.tAndEApprover || accessTo.tAndEContractor) && showMenu
    }
  ];

  const allUtilMenus = [
    {
      name: 'Profile',
      path: '/my-profile',
      icon: <AccountCircleOutlined sx={{ fontSize: '28px' }} />,
      show: !accessTo.tAndEApprover && width >= maxMobileSize && isAuthenticated && showMenu
    },
    {
      name: 'Settings',
      path: '/dashboard/settings',
      icon: <SettingsOutlined sx={{ fontSize: width >= maxMobileSize ? '28px' : '24px' }} />,
      show: !accessTo.tAndEApprover && isAuthenticated && showMenu
    },
    {
      name: 'Support',
      target: '_blank',
      path: 'https://www.airswift.com/en/knowledge',
      icon: <HelpOutlineOutlined sx={{ fontSize: width >= maxMobileSize ? '28px' : '24px' }} />,
      show: showMenu
    }
  ];

  const menuItems = allMenuItems.filter((item) => item.show);
  const utilMenus = allUtilMenus.filter((item) => item.show);

  const handleClickOnCounter = () => {
    const path = window.location.pathname;
    if (path.includes('/timesheet/')) {
      navigate('/dashboard/timesheets?status=Pending');
      return;
    }
    if (path.includes('/expense/')) {
      navigate('/dashboard/expenses?status=Pending');
      return;
    }

    setSearchParams({ status: 'Pending' });
  };

  useEffect(() => {
    if (accessTo.tAndEApprover) {
      dispatch(getTandEPendingCounts());
    }
  }, [accessTo]);

  const getAreaLable = () => {
    const path = window.location.pathname;

    if (path === '/') return 'Airswift';

    if (path.includes('/my-profile')) {
      return t('common.navigationheaders.myprofile');
    }

    if (path.includes('/jobs/') || path.includes('/Jobs/')) {
      return t('common.header.menu.jobs');
    }

    if (path.includes('/timesheet/')) {
      return (
        <>
          {t('common.navigationheaders.timesheet')}
          {pendingTandECounts?.PendingTimesheetCount ? (
            <span className={classes.withCursor} role="presentation" onClick={handleClickOnCounter}>
              <Typography display="inline" mx={3} sx={{ borderLeft: 1, borderColor: colors.LIGHT_GREY }} />
              <strong> {pendingTandECounts?.PendingTimesheetCount}</strong> pending
            </span>
          ) : null}
        </>
      );
    }

    if (path.includes('/expense/')) {
      return (
        <>
          {t('common.navigationheaders.expense')}
          {pendingTandECounts?.PendingClaimsCount ? (
            <span className={classes.withCursor} role="presentation" onClick={handleClickOnCounter}>
              <Typography display="inline" mx={3} sx={{ borderLeft: 1, borderColor: colors.LIGHT_GREY }} />
              <strong> {pendingTandECounts?.PendingClaimsCount} </strong> pending
            </span>
          ) : null}
        </>
      );
    }

    switch (path.split('/dashboard')[1]) {
      case '/profile-builder-one':
      case '/profile-builder-two':
      case '/profile-builder-three':
      case '/profile-builder-four':
      case '/profile-builder-five':
      case '/profile-builder-six':
      case '/profile-builder-seven':
        return t('common.navigationheaders.profilebuilder');
      case '/tifo':
        return t('common.navigationheaders.onboarding');
      case '/expenses':
        return (
          <>
            {accessTo.tAndEApprover
              ? t('common.navigationheaders.expensesapprover')
              : t('common.navigationheaders.expenses')}
            {pendingTandECounts?.PendingClaimsCount ? (
              <span className={classes.withCursor} role="presentation" onClick={handleClickOnCounter}>
                <Typography display="inline" mx={3} sx={{ borderLeft: 1, borderColor: colors.LIGHT_GREY }} />
                <strong> {pendingTandECounts?.PendingClaimsCount} </strong> pending
              </span>
            ) : null}
          </>
        );
      case '/timesheets':
        return (
          <>
            {accessTo.tAndEApprover
              ? t('common.navigationheaders.timesheetsapprover')
              : t('common.navigationheaders.timesheets')}
            {pendingTandECounts?.PendingTimesheetCount ? (
              <span className={classes.withCursor} role="presentation" onClick={handleClickOnCounter}>
                <Typography display="inline" mx={3} sx={{ borderLeft: 1, borderColor: colors.LIGHT_GREY }} />
                <strong> {pendingTandECounts?.PendingTimesheetCount}</strong> pending
              </span>
            ) : null}
          </>
        );
      case '/jobs':
      case '/Jobs':
        return t('common.navigationheaders.jobs');
      case '/settings':
        return t('common.navigationheaders.settings');
      case '/create-timesheet':
        return t('common.navigationheaders.createtimesheet');
      case '/create-claim':
        return t('common.navigationheaders.createclaim');
      default:
        return t('common.navigationheaders.dashboard');
    }
  };

  const handleOnClickAuthentication = () => {
    setIsBeingSignedIn(true);
    if (isAuthenticated) {
      handleLogout(instance);
    } else {
      handleLogin(instance);
    }
  };

  return (
    <div className={classes.headerContainer}>
      <AppBar component="nav" sx={{ backgroundColor: 'white', height: width >= maxMobileSize ? '60px' : '48px' }}>
        <header className="bg-white shadow-sm">
          <div className="max-w-6xl mx-auto flex items-center justify-between h-12 md:h-16 px-4">
            <div className="flex items-center space-x-4">
              <Link to={isAuthenticated ? '/dashboard' : '/'}>
                <img src={logo} alt="Airswift Logo" className="h-8 md:h-10 w-auto" />
              </Link>
            </div>

            <nav className="flex items-center space-x-6 md:space-x-8 text-gray-600 font-medium">
              {width >= maxMobileSize &&
                menuItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.name}
                      to={item.path}
                      className={`flex flex-col hover:!text-[#022D41] items-center justify-center ${
                        isActive ? '!text-[#022D41]' : '!text-gray-500'
                      }`}
                    >
                      <div className="flex flex-col items-center gap-0.5">
                        <span className="material-icons">{item.icon}</span>
                        <span className="text-xs">{item.name}</span>
                      </div>
                      <span
                        className={`h-0.5 w-[calc(100%+20px)] transition-all border-b-2 ${
                          isActive ? 'border-navy-blue' : 'border-transparent'
                        } relative top-[8px]`}
                      />
                    </Link>
                  );
                })}
              {width >= maxMobileSize && showMenu && <div className="bg-gray-300 h-[40px] w-0.5" />}
              {utilMenus.map((item) => {
                const isActive = location.pathname === item.path;
                const isExternal = item.target === '_blank';

                const content = (
                  <div className="flex flex-col items-center gap-0.5">
                    <span className="material-icons">{item.icon}</span>
                    <span className="text-xs">{width >= maxMobileSize ? item.name : ''}</span>
                  </div>
                );

                const underline = (
                  <span
                    className={`h-0.5 w-[calc(100%+20px)] border-b-2 ${
                      isActive ? 'border-navy-blue' : 'border-transparent'
                    } relative top-[8px]`}
                  />
                );

                return isExternal ? (
                  <a
                    key={item.name}
                    href={item.path}
                    target={item.target}
                    rel="noopener noreferrer"
                    className="flex flex-col hover:!text-[#022D41] items-center justify-center !text-gray-500"
                  >
                    {content}
                    {underline}
                  </a>
                ) : (
                  <Link
                    key={item.name}
                    to={item.path}
                    className={`flex flex-col hover:!text-[#022D41] items-center justify-center ${
                      isActive ? '!text-[#022D41]' : '!text-gray-500'
                    }`}
                  >
                    {content}
                    {underline}
                  </Link>
                );
              })}

              {showMenu && <LanguageSwitcher />}
              {showMenu && (
                <button
                  onClick={() => handleOnClickAuthentication()}
                  className="cursor-pointer flex flex-col items-center hover:!text-[#022D41] !text-gray-500 gap-0.5"
                >
                  <span className="material-icons">
                    {isAuthenticated && <Logout sx={{ fontSize: width >= maxMobileSize ? '28px' : '24px' }} />}
                    {isBeingSignedIn && <CircularProgress size="1rem" sx={{ color: colors.NAVY_BLUE }} />}
                    {!isAuthenticated && !isBeingSignedIn && (
                      <Login sx={{ fontSize: width >= maxMobileSize ? '28px' : '24px' }} />
                    )}
                  </span>
                  {isAuthenticated ? (
                    <span className="text-xs">{width >= maxMobileSize ? 'Sign Out' : ''}</span>
                  ) : (
                    <span className="text-xs">{width >= maxMobileSize ? 'Sign In' : ''}</span>
                  )}
                </button>
              )}
            </nav>
          </div>
        </header>
      </AppBar>
    </div>
  );
}
